import type { ReviewUnit } from "../../../api/content/curationApi";
import { plainTriple, type PlainTerm } from "../diff/plainTriples";

/**
 * A term unit's statements as a before → after table, one row per field (CU-3), and the
 * statements that are not a field of the term (blank-node structures, statements about
 * other subjects in the same axiom). Pure.
 */
export interface FieldChange {
  readonly predicateKey: string | null;
  readonly predicateName: string;
  readonly predicate: string;
  readonly before: readonly PlainTerm[];
  readonly after: readonly PlainTerm[];
}

export interface TermFieldChanges {
  readonly fields: readonly FieldChange[];
  readonly otherAdded: readonly string[];
  readonly otherRemoved: readonly string[];
  /** Named parents the unit adds (`rdfs:subClassOf`, `rdfs:subPropertyOf`). */
  readonly addedParents: readonly PlainTerm[];
}

const PREDICATE = /^<[^>]*> <([^>]*)>/;

export function termFieldChanges(unit: ReviewUnit, labels: Readonly<Record<string, string>>): TermFieldChanges {
  const fields = new Map<string, { predicateKey: string | null; predicateName: string; before: PlainTerm[]; after: PlainTerm[] }>();
  const otherAdded: string[] = [];
  const otherRemoved: string[] = [];
  const subject = unit.term === null ? null : `<${unit.term}> `;
  const add = (line: string, side: "before" | "after", other: string[]) => {
    const triple = plainTriple(line, labels);
    const predicate = PREDICATE.exec(line)?.[1];
    if (subject === null || !line.startsWith(subject) || triple === null || predicate === undefined || triple.object.kind === "axiom") {
      other.push(line);
      return;
    }
    const row = fields.get(predicate) ?? { predicateKey: triple.predicateKey, predicateName: triple.predicateName, before: [], after: [] };
    row[side].push(triple.object);
    fields.set(predicate, row);
  };
  for (const line of unit.removedTriples) {
    add(line, "before", otherRemoved);
  }
  for (const line of unit.addedTriples) {
    add(line, "after", otherAdded);
  }
  const rows = [...fields.entries()].map(([predicate, row]) => ({ predicate, ...row }));
  const parents = rows.filter((row) => row.predicateKey === "subClassOf" || row.predicateKey === "subPropertyOf").flatMap((row) => row.after);
  return {
    fields: rows.sort((left, right) => (left.predicateKey === "type" ? -1 : right.predicateKey === "type" ? 1 : left.predicateName.localeCompare(right.predicateName))),
    otherAdded,
    otherRemoved,
    addedParents: parents,
  };
}

export function plainTermText(term: PlainTerm): string {
  switch (term.kind) {
    case "term":
      return term.label ?? term.name;
    case "axiom":
      return term.label;
    case "text":
      return term.language !== null ? `“${term.text}” (${term.language})` : `“${term.text}”`;
  }
}
